import { ApolloQueryResult } from "@apollo/client/core";
import { nextTick, ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getChainPaymentMethod } from "@/common/graphql/chain";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetGroup,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import type {
  DeliveryRequestActionsPrintShippingDataItem,
  ListData,
  UseDeliveryRequestActionsParams,
} from "../types";
import type TagLabelPrintDialog from "@/components/TagLabelPrintDialog.vue";
import type SlipLabelPrintDialog from "@/components/SlipLabelPrintDialog.vue";
import {
  DeleteDeliveryRequestDocument,
  DeleteDeliveryRequestMutation,
  DeleteDeliveryRequestMutationVariables,
  DeliveryRequestUpdateInput,
  GetDeliveryRequestDocument,
  GetDeliveryRequestQuery,
  GetDeliveryRequestQueryVariables,
  OperationInput,
} from "@/generated/graphql";

const createLogInput = (): OperationInput => ({
  area_code: "10",
  detail: "操作詳細",
  display_category: "画面名",
  operation: "操作",
  user_id: localStorageGetUserId(),
});

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestActionsState = () => {
  const selectListCheckbox = ref<ListData[]>([]);
  const filteredSelectListCheckbox = (): ListData[] =>
    selectListCheckbox.value.filter((value) => value.cancelAllowed);

  return {
    selectListCheckbox,
    filteredSelectListCheckbox,
  };
};

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliveryRequestActions = ({
  confirmDeleteDialog,
  confirmTagPrintDialog,
  confirmSlipPrintDialog,
  overlay,
  errdialog,
  errMessage,
  editPage,
  confirmPage,
  selectListCheckbox,
  filteredSelectListCheckbox,
  captureListPageForReturn,
  searchQuery,
  openConfirmDeletePage,
  openViewPage,
  editDeliveryRequest,
  isPersonalInfoPossible,
  listLoading,
  listDataArray,
  errorZipCode,
  listOptions,
  targetShopCode,
  delivery_type,
  originalAddress,
  zipCodeFromPref,
  listDeliveryArea,
  getAreaList,
  onClickListDeliveryArea,
  paymentList,
  paymentMethodItems,
  onChangePaymentMethod,
  commmaMoney,
  areaClear,
  acceptableEndTime,
  deliveryId_0_Flg,
  overDeliveryEndTime,
  doubleDeliOriconCount,
  isDoubleDeli,
  charterDeliOriconCount,
  isCharterDeli,
  deliveryAvailabilityHeader,
  deliveryAvailabilityData,
  disp_deliveryAvailabilityData,
  deliveryStartTime,
  deliveryEndTime,
  deliveryStartHourItem,
  deliveryStartHourValues,
  deliveryStartMinitItem,
  deliveryStartMinitValues,
  befor_data,
  irregularDeliveryStartTime,
  irregularDeliveryEndTime,
  initializeEditDeliveryTime,
  pre_double_deli_oricon_flg,
  pre_charter_deli_oricon_flg,
  displayDeliveryTime,
  house_card_befor,
  house_card_change,
  isDisabled,
  dummy_payment_amount,
  listDataTotalCount,
  is_delivery_address1_display,
  wordList,
  shopAddress,
}: UseDeliveryRequestActionsParams) => {
  const tagLabelPrintDialogRef = ref<InstanceType<typeof TagLabelPrintDialog> | null>(null);
  const slipLabelPrintDialogRef = ref<InstanceType<typeof SlipLabelPrintDialog> | null>(null);
  const slipPrintShippingData = ref<DeliveryRequestActionsPrintShippingDataItem[]>([]);

  const deleteExecute = async (reception_no: string): Promise<boolean> => {
    const apolloClient = await generateApolloClient();
    try {
      await apolloClient.mutate<
        DeleteDeliveryRequestMutation,
        DeleteDeliveryRequestMutationVariables
      >({
        mutation: DeleteDeliveryRequestDocument,
        variables: {
          logInput: createLogInput(),
          reception_no,
        },
      });
      return true;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "配達依頼のキャンセルに失敗しました。";
      return false;
    }
  };

  const onClickCheckDelete = async (): Promise<void> => {
    if (filteredSelectListCheckbox().length === 0) return;
    confirmDeleteDialog.value = true;
  };

  const onClickCheckDeleteComplete = async (): Promise<void> => {
    overlay.value = true;

    let result = false;
    const arrayError: string[] = [];
    for (const val of filteredSelectListCheckbox()) {
      if (await deleteExecute(val.receptionNo)) {
        result = true;
      } else {
        arrayError.push(val.receptionNo);
      }
    }

    confirmDeleteDialog.value = false;
    overlay.value = false;

    if (result) {
      await nextTick(async () => {
        selectListCheckbox.value = [];
        await searchQuery();
      });
      return;
    }

    errMessage.value = `受付番号 ${arrayError.join(",")}のキャンセルに失敗しました。`;
    errdialog.value = true;
  };

  const onClickDelete = async (listData: ListData): Promise<void> => {
    await onClickEdit(listData.receptionNo, listData.editAllowed);
    openConfirmDeletePage();
  };

  const onClickView = async (listData: ListData): Promise<void> => {
    await onClickEdit(listData.receptionNo, listData.editAllowed);
    openViewPage();
  };

  const onClickEditOrView = async (listData: ListData): Promise<void> => {
    // 本部権限（group=1）は個人情報表示不可のため、editAllowed=2（金額のみ編集可）は個人情報権限に関わらず編集可
    const isHq = localStorageGetGroup() === "1";
    if (
      (listData.editAllowed === 1 && isPersonalInfoPossible.value === true) ||
      (listData.editAllowed === 2 && (isPersonalInfoPossible.value === true || isHq))
    ) {
      await onClickEdit(listData.receptionNo, listData.editAllowed);
    } else {
      await onClickView(listData);
    }
  };

  const onClickEdit = async (receptionNo: string, editAllowed: number): Promise<void> => {
    overlay.value = true;
    editPage.value = true;
    // 編集前の一覧ページ番号を保存し、戻る際に復元できるようにする
    captureListPageForReturn();

    const apolloClient = await generateApolloClient();

    listLoading.value = false;
    acceptableEndTime.value = "";
    deliveryStartHourItem.value = [];
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitItem.value = [
      { label: "00", deliveryStartMinit: 0 },
      { label: "30", deliveryStartMinit: 1 },
    ];
    deliveryStartMinitValues.value = undefined;
    befor_data.delivery_date = "";
    befor_data.delivery_id = 999;
    befor_data.irregularDeliveryStartTime = [];
    irregularDeliveryStartTime.value = "";
    irregularDeliveryEndTime.value = "";
    deliveryId_0_Flg.value = false;
    overDeliveryEndTime.value = false;
    deliveryStartTime.value = "";
    deliveryEndTime.value = "";
    doubleDeliOriconCount.value = null;
    isDoubleDeli.value = false;
    pre_double_deli_oricon_flg.value = 0;
    charterDeliOriconCount.value = null;
    isCharterDeli.value = false;
    pre_charter_deli_oricon_flg.value = 0;
    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.value = [];
    disp_deliveryAvailabilityData.value = [];
    displayDeliveryTime.value = "";
    listDataArray.value = [];
    listDataTotalCount.value = 0;
    selectListCheckbox.value = [];
    errorZipCode.value = "";
    targetShopCode.value = "";

    const logInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    let result: ApolloQueryResult<GetDeliveryRequestQuery>;
    try {
      result = await apolloClient.query<GetDeliveryRequestQuery, GetDeliveryRequestQueryVariables>({
        query: GetDeliveryRequestDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          is_delivery_address1_display: is_delivery_address1_display.value,
          logInput,
          reception_no: receptionNo,
        },
      });
      targetShopCode.value = result.data.getDeliveryRequest!.shop_code;
    } catch (err) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = "配達依頼一覧データの取得に失敗しました。";
      editDeliveryRequest.value = null;
      overlay.value = false;
      return;
    }

    if (!result.data.getDeliveryRequest) {
      errdialog.value = true;
      errMessage.value = "配達依頼一覧データの取得に失敗しました。";
      editDeliveryRequest.value = null;
      overlay.value = false;
      return;
    }

    editDeliveryRequest.value = JSON.parse(
      JSON.stringify(result.data.getDeliveryRequest)
    ) as DeliveryRequestUpdateInput;
    delivery_type.value = result.data.getDeliveryRequest.delivery_type;

    try {
      if (!editDeliveryRequest.value) {
        throw new Error("配送依頼データの取得に失敗しました。");
      }
      originalAddress.value = editDeliveryRequest.value.delivery_address1 ?? "";
      zipCodeFromPref.value = editDeliveryRequest.value.delivery_zip_code ?? "";
      await getAreaList(
        "",
        editDeliveryRequest.value.shop_code,
        editDeliveryRequest.value.area_code,
        editDeliveryRequest.value.chain_code
      );

      if (listDeliveryArea.value && editDeliveryRequest.value) {
        for (let i = 0; i < listDeliveryArea.value.length; i++) {
          if (listDeliveryArea.value[i]?.area_code === editDeliveryRequest.value.area_code) {
            await onClickListDeliveryArea(listDeliveryArea.value[i]!);
          }
        }
      }
      originalAddress.value = "";

      paymentList.value = await getChainPaymentMethod(
        localStorageGetCustomerCode(),
        editDeliveryRequest.value.chain_code ?? ""
      );
      paymentMethodItems.value = paymentList.value!.map((p) => p?.payment_method);
      if (paymentMethodItems.value.length === 1) {
        await onChangePaymentMethod(paymentMethodItems.value[0]);
      }
    } catch (err) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エリア情報の取得に失敗しました。";
      await areaClear();
      overlay.value = false;
      return;
    }

    const requiedDate =
      editDeliveryRequest.value?.required_delivery_date
        ? editDeliveryRequest.value.required_delivery_date.substring(0, 10)
        : "";
    const strDeliveryId = await initializeEditDeliveryTime(requiedDate);

    pre_double_deli_oricon_flg.value = editDeliveryRequest.value?.double_deli_oricon_flg ?? 0;
    pre_charter_deli_oricon_flg.value = editDeliveryRequest.value?.charter_deli_oricon_flg ?? 0;
    displayDeliveryTime.value = requiedDate ? `${requiedDate}  ${strDeliveryId}` : "";

    await onChangePaymentMethod(editDeliveryRequest.value?.payment_method ?? "");
    dummy_payment_amount.value =
      editDeliveryRequest.value?.payment_amount != null
        ? String(editDeliveryRequest.value.payment_amount).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,")
        : "";
    await commmaMoney(String(editDeliveryRequest.value?.payment_amount ?? ""));

    house_card_befor.value = editDeliveryRequest.value?.house_card ?? "";
    house_card_change.value = house_card_befor.value !== "";
    isDisabled.value = editAllowed === 2;
    editPage.value = true;
    overlay.value = false;
  };

  const onClickCheckTagPrint = (): void => {
    confirmTagPrintDialog.value = true;
  };

  const onClickCheckSlipPrint = (): void => {
    confirmSlipPrintDialog.value = true;
  };

  const isCheckedSelectListCheckbox = (): boolean => filteredSelectListCheckbox().length > 0;

  const onClickTagPrintPreview = async (): Promise<void> => {
    if (!isCheckedSelectListCheckbox()) return;

    onClickCheckTagPrint();
    await nextTick();

    const tagLabelPrintDialog = tagLabelPrintDialogRef.value;
    const content = tagLabelPrintDialog?.printableContent?.innerHTML;
    if (content) {
      tagLabelPrintDialog.showPrintTab(content);
    }
    confirmTagPrintDialog.value = false;
  };

  const onClickSlipPrintPreview = async (): Promise<void> => {
    if (!isCheckedSelectListCheckbox()) return;

    slipPrintShippingData.value = filteredSelectListCheckbox().map((item) => ({
      receptionNo: item.receptionNo,
      receptionTimedelivery: item.receptionDate,
      clientName: `${item.name} 様(${item.nameKana})`,
      deliveryAddress: item.deliveryAddress,
      clientTel: item.deliveryPhone,
      deliveryEstimatedDate: item.deliveryEstimatedDate,
      deliveryId: item.deliveryId,
      plannedDeliveryTime: item.deliveryTime,
      unit: item.unit,
      shopName: item.shopName,
      memoDelivery: item.deliveryNotes,
      shopAddress: shopAddress(),
      lblTransportationCost: wordList.lbl_transportation_cost ?? "",
    }));

    onClickCheckSlipPrint();
    await nextTick(async () => {
      const slipLabelPrintDialog = slipLabelPrintDialogRef.value;
      if (slipLabelPrintDialog?.printableContent) {
        const content = slipLabelPrintDialog.printableContent.innerHTML;
        slipLabelPrintDialog.showPrintTab(content);
      }
      confirmSlipPrintDialog.value = false;
    });
  };

  const onClickConfirmDeleteOk = async (): Promise<void> => {
    if (!editDeliveryRequest.value) return;

    overlay.value = true;
    if (await deleteExecute(editDeliveryRequest.value.reception_no)) {
      await nextTick(async () => {
        await new Promise((resolve) => setTimeout(resolve, 800));
      });
      editPage.value = false;
      confirmPage.value = false;
      overlay.value = false;
      return;
    }

    errdialog.value = true;
    errMessage.value = "キャンセル処理に失敗しました。";
    editPage.value = false;
    overlay.value = false;
  };

  return {
    tagLabelPrintDialogRef,
    slipLabelPrintDialogRef,
    slipPrintShippingData,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
    onClickDelete,
    onClickView,
    onClickEdit,
    onClickEditOrView,
    onClickCheckTagPrint,
    onClickCheckSlipPrint,
    onClickTagPrintPreview,
    onClickSlipPrintPreview,
    onClickConfirmDeleteOk,
  };
};
